import type {
  AddBalancePostingsCommandData,
  FractionBalancePostingSpec,
} from '@festina/life-general-types/life'
import { notEmpty, typeUtils } from '@festina-life/core'
import {
  useFormikContext,
  usePercentageFormatter,
  useTranslate,
  useTranslateEnum,
} from '@festina-life/ui/hooks'
import {
  createColumnDefiner,
  SortableTableSearch,
} from '@festina-life/ui/listsAndTables'
import { useField } from 'formik'
import { useState } from 'react'

import { useRowActions } from '../../../hooks/useRowActions'
import { useTranslateCover } from '../../../hooks/useTranslateCover'
import { useHook } from '../../../lenteHooks'
import { FractionBalancePostingSpecModal } from './FractionBalancePostingSpecModal'
import { FractionBalancePostingSpecTableAction } from './FractionBalancePostingSpecTableAction'

const defineColumn = createColumnDefiner<FractionBalancePostingSpec>()

const validateKey = typeUtils.createValidator<AddBalancePostingsCommandData>()

export const FractionBalancePostingSpecsTable = () => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const { formatPercentWithDecimals } = usePercentageFormatter()

  const renderPolicy = useHook('pages.commitment.useLazyRenderPolicy')()
  const translateCover = useTranslateCover()

  const [editing, setEditing] = useState<FractionBalancePostingSpec>()

  const [{ value }, , { setValue }] = useField<
    AddBalancePostingsCommandData['fractionBalancePostingSpecs']
  >(validateKey('fractionBalancePostingSpecs'))

  const {
    status: { shouldFieldsBeDisabled },
  } = useFormikContext()

  const handleAddFractionBalancePostingSpec = (
    fractionBalancePostingSpec: FractionBalancePostingSpec,
  ) => setValue((value ?? []).concat(fractionBalancePostingSpec))

  const handleEditFractionBalancePostingSpec = async (
    fractionBalancePostingSpec: FractionBalancePostingSpec,
  ) => {
    await setValue(
      value?.map((spec) =>
        spec === editing ? fractionBalancePostingSpec : spec,
      ),
    )
    setEditing(undefined)
  }

  const handleDeleteFractionBalancePostingSpec = (
    fractionBalancePostingSpec: FractionBalancePostingSpec,
  ) => setValue(value?.filter((spec) => spec !== fractionBalancePostingSpec))

  const columns = {
    accountType: defineColumn({
      heading: translate('action.addBalancePosting.field.accountType'),
      keyPath: 'accountType',
      formatter: (x) => translateEnum('life.enum.AccountType', x),
    }),
    fraction: defineColumn({
      heading: translate('action.addBalancePosting.field.fraction'),
      keyPath: 'fraction',
      formatter: formatPercentWithDecimals,
      alignment: 'right',
    }),
    coverKey: defineColumn({
      heading: translate('action.addBalancePosting.field.coverKey'),
      keyPath: 'coverKey',
      formatter: (coverKey) => translateCover(coverKey),
    }),
    policeKey: defineColumn({
      heading: translate('action.addBalancePosting.field.policeKey'),
      keyPath: 'policeKey', // translation typo intentionally matches field name
      formatter: renderPolicy,
    }),
    postingType: defineColumn({
      heading: translate('action.addBalancePosting.field.postingType'),
      keyPath: 'postingType',
      formatter: (x) => translateEnum('life.enum.PostingType', x),
    }),
  }

  const rowActions = useRowActions<FractionBalancePostingSpec>((row) => ({
    onEdit: () => setEditing(row),
    onDelete: () => handleDeleteFractionBalancePostingSpec(row),
  }))

  return (
    <>
      {editing && (
        <FractionBalancePostingSpecModal
          initialValues={editing}
          onSubmit={handleEditFractionBalancePostingSpec}
          onClose={() => setEditing(undefined)}
        />
      )}
      <SortableTableSearch
        title={translate(
          'action.addBalancePosting.field.fractionBalancePostingSpecs',
        )}
        columns={columns}
        data={value?.filter(notEmpty) ?? []}
        actions={
          shouldFieldsBeDisabled ? undefined : (
            <FractionBalancePostingSpecTableAction
              onSubmit={handleAddFractionBalancePostingSpec}
            />
          )
        }
        rowActions={shouldFieldsBeDisabled ? undefined : rowActions}
      />
    </>
  )
}
